import { useMemo } from 'react'
import {
  Dialog,
  Box,
  Text,
  TooltipRenderer,
  IconInfo,
  TextLinkButton,
  Table,
  Bleed
} from '@machobear/design-system'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { path, pipe, equals } from 'ramda'
import formatCurrency from '@/utils/format-currency'
import { showTenantVerificationModal } from '@/features/Chat/tenant-verification-modal'
import VerifiedIcon from './verified-icon.svg?react'

const StyledVerifiedIcon = styled(VerifiedIcon)`
  cursor: pointer;
  height: 23px;
  margin-left: 4px;
  flex-shrink: 0;
`

type Props = {
  visible: boolean
  closeModal: () => void
  data: any
  navigateToDocuments: () => void
  application: any
}

const BreakdownDialog = ({
  visible,
  closeModal,
  data,
  navigateToDocuments,
  application
}: Props) => {
  const { t } = useTranslation()
  const identityVerifiedStatus = pipe(
    path(['trust_score', 'trustworthiness', 'identity', 'details', 0, 'status'])
  )(application)
  const identityVerified = pipe(equals('verified'))(identityVerifiedStatus as string)
  const incomeStatus = pipe(
    path(['trust_score', 'trustworthiness', 'income', 'details', 0, 'status'])
  )(application)

  const getLink = (status: string) => {
    switch (status) {
      case 'verified':
        return (
          <TextLinkButton onClick={navigateToDocuments} weight="regular">
            {t('l.application.view.view_document.label', 'View Document')}
          </TextLinkButton>
        )
      case 'removed':
        return (
          <TooltipRenderer
            id="income_document_removed"
            tooltip={
              <Box paddingY="xxxsmall" justifyContent="center">
                <Text weight="strong" size="small">
                  {t(
                    'b.chat.view.document_removed.button',
                    'We can’t display this document as it has either been deleted by the applicant, or we’ve removed it due to it not meeting liv.rent’s verification requirements.'
                  )}
                </Text>
              </Box>
            }
          >
            {({ triggerProps }) => (
              <Box {...triggerProps} cursor="pointer">
                <Text size="small" tone="tertiary" baseline={false}>
                  {t('l.application.view.view_document.label', 'View Document')}
                </Text>
              </Box>
            )}
          </TooltipRenderer>
        )
      case 'pending':
        return (
          <TooltipRenderer
            id="income_document_pending"
            tooltip={
              <Box paddingY="xxxsmall" justifyContent="center">
                <Text weight="strong" size="small">
                  {t(
                    'b.chat.view.document_pending.button',
                    'We can’t display this document as it is still pending verification. It should be available to view in 1-3 business days.'
                  )}
                </Text>
              </Box>
            }
          >
            {({ triggerProps }) => (
              <Box
                {...triggerProps}
                cursor="pointer"
                display="flex"
                style={{ width: 'fit-content' }}
              >
                <Text size="small" tone="tertiary" baseline={false}>
                  {t('l.application.view.view_document.label', 'View Document')}
                </Text>
              </Box>
            )}
          </TooltipRenderer>
        )

      default:
        return null
    }
  }

  const columns = [
    {
      accessor: 'name',
      gridWidth: 'fill-available',
      Header: t('l.application.view.applicant_name.label', 'Applicant Name'),
      Cell: ({ row: { original } }: any) => {
        const isMainApplicant = original?.is_roommate === false
        const showVerified = isMainApplicant && identityVerified

        const showModal = () =>
          showTenantVerificationModal({
            verificationStatus: identityVerifiedStatus,
            timelineRoomId: null
          })
        return (
          <Bleed
            vertical={original?.isTotal ? 'small' : 'none'}
            horizontal={original?.isTotal ? 'xsmall' : 'none'}
          >
            <Box
              background={original?.isTotal ? 'neutralSoft' : 'white'}
              paddingX={original?.isTotal ? 'xsmall' : 'none'}
              paddingY={original?.isTotal ? 'small' : 'none'}
            >
              <Box display="flex">
                <Text tone="neutral" baseline={false}>
                  {original?.applicant_name}
                </Text>
                {showVerified && <StyledVerifiedIcon onClick={showModal} />}
              </Box>

              {!original?.isTotal && (
                <Text size="small" tone="tertiary" baseline={false}>
                  {isMainApplicant
                    ? t('l.application.view.main_applicant.label', 'Main Applicant')
                    : t(`basic:b.contact.relationships.${original?.relationship}.text`)}
                </Text>
              )}
            </Box>
          </Bleed>
        )
      }
    },
    {
      accessor: 'income',
      gridWidth: 'fill-available',
      Header: t('l.application.view.monthly_income.label', 'Monthly Income'),
      Cell: ({ row: { original } }: any) => {
        const isMainApplicant = original?.is_roommate === false

        return (
          <Bleed
            vertical={original?.isTotal ? 'small' : 'none'}
            horizontal={original?.isTotal ? 'xsmall' : 'none'}
          >
            <Box
              display="flex"
              flexDirection="column"
              background={original?.isTotal ? 'neutralSoft' : 'white'}
              paddingX={original?.isTotal ? 'xsmall' : 'none'}
              paddingY={original?.isTotal ? 'small' : 'none'}
            >
              <Box display="flex" alignItems="center" gap="xxxsmall">
                <Text
                  tone="neutral"
                  baseline={false}
                  weight={original?.isTotal ? 'strong' : 'regular'}
                >
                  {original?.is_prepaid
                    ? 'Prepay Rent'
                    : formatCurrency(original?.monthly_income, { hideCents: true })}
                </Text>
                {!isMainApplicant && !original?.isTotal && (
                  <TooltipRenderer
                    id="income_info"
                    tooltip={
                      <Box paddingY="xxxsmall" justifyContent="center">
                        <Text weight="strong" size="small">
                          {t(
                            'b.chat.view.self_reported.button',
                            'This applicant’s income is self-reported.'
                          )}
                        </Text>
                      </Box>
                    }
                  >
                    {({ triggerProps }) => (
                      <Box {...triggerProps}>
                        <IconInfo />
                      </Box>
                    )}
                  </TooltipRenderer>
                )}
              </Box>
              {isMainApplicant && getLink(incomeStatus as string)}
            </Box>
          </Bleed>
        )
      }
    }
  ]

  const totalMonthlyIncome = data.reduce((acc: number, curr: { monthly_income: number }) => {
    return acc + curr?.monthly_income
  }, 0)

  const totalData = [
    ...data,
    {
      applicant_name: 'Total Monthly Income',
      is_prepaid: false,
      is_roommate: true,
      monthly_income: totalMonthlyIncome,
      relationship: '',
      isTotal: true
    }
  ]

  return (
    <Dialog
      id="breakdownDialog"
      open={visible}
      onClose={closeModal}
      width="small"
      title={t('l.application.rent_to_income.breakdown.title', 'Total Monthly Income Breakdown')}
    >
      <Table data={totalData} columns={columns} />
    </Dialog>
  )
}

export default BreakdownDialog
